import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEdit, FaTrashAlt, FaListUl } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import FinanceBreadcrumb from "../../components/finance/Breadcrumb";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Chart of Accounts — the master list of GL accounts every journal entry
 * posts against. Phase A of the Finance & Accounting module.
 */

export const ACCOUNT_TYPES = [
  { value: "asset", label: "Asset" },
  { value: "liability", label: "Liability" },
  { value: "equity", label: "Equity" },
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
];

const authPayload = () => ({
  customer_id: Number(localStorage.getItem("customer_id")),
  api_key: localStorage.getItem("api_key"),
  user_id: Number(localStorage.getItem("user_id")),
  language: "en",
});

const emptyForm = {
  account_code: "",
  account_name: "",
  account_type: "asset",
  parent_account_id: "",
  account_description: "",
  is_active: "1",
  is_cash_account: false,
};

export function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  const [displayModal, setDisplayModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Account ledger drill-in (reports/account-ledger)
  const [showLedger, setShowLedger] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFrom, setLedgerFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [ledgerTo, setLedgerTo] = useState(new Date().toISOString().slice(0, 10));

  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");
  const money = (v) => `${customer_currency} ${formatNumber(v || 0)}`;

  const openLedger = (record) => {
    setLedgerAccount(record);
    setLedgerData(null);
    setShowLedger(true);
    fetchLedger(record.account_id, ledgerFrom, ledgerTo);
  };

  const fetchLedger = async (accountId, from, to) => {
    setLedgerLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.accountLedger,
        JSON.stringify({
          ...authPayload(),
          account_id: accountId,
          from_date: from,
          to_date: to,
        })
      );
      setLedgerData(res?.result ? res.data || null : null);
      if (!res?.result) toast.error(res?.message || "Failed to load ledger.");
    } catch (err) {
      console.error("Account ledger error:", err);
      toast.error("An error occurred loading the ledger.");
    } finally {
      setLedgerLoading(false);
    }
  };

  /* ---------------- FETCH LIST ---------------- */
  const fetchAccounts = async () => {
    setLoading(true);

    // Type is filtered client-side so the stat-card counts stay accurate while
    // a type is selected. The chart of accounts is a small, bounded list.
    const requestBody = {
      ...authPayload(),
      account_type: "",
      search: search,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.coaList,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        setAccounts(res.data || []);
      } else {
        setAccounts([]);
        toast.error(res?.message || "Failed to load chart of accounts");
      }
    } catch {
      setAccounts([]);
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAccounts();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const errors = {};
    if (!form.account_code.trim()) errors.account_code = "Account code is required";
    if (!form.account_name.trim()) errors.account_name = "Account name is required";
    if (!form.account_type) errors.account_type = "Account type is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---------------- CREATE / UPDATE ---------------- */
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const requestBody = {
      ...authPayload(),
      account_code: form.account_code.trim(),
      account_name: form.account_name.trim(),
      account_type: form.account_type,
      parent_account_id: form.parent_account_id
        ? Number(form.parent_account_id)
        : null,
      account_description: form.account_description,
      is_active: Number(form.is_active),
      is_cash_account: form.is_cash_account ? 1 : 0,
      ...(editingRecord && { account_id: editingRecord.account_id }),
    };

    const api = editingRecord ? ApiConfig.coaUpdate : ApiConfig.coaCreate;

    try {
      const res = await simplePostCall(api, JSON.stringify(requestBody));
      if (res?.result) {
        toast.success(res.message || "Account saved");
        setDisplayModal(false);
        fetchAccounts();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      const res = await simplePostCall(
        ApiConfig.coaDelete,
        JSON.stringify({ ...authPayload(), account_id: deleteId })
      );

      if (res?.result) {
        toast.success(res.message || "Account deleted");
        fetchAccounts();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /* ---------------- HELPERS ---------------- */
  const openCreate = () => {
    setEditingRecord(null);
    setForm(emptyForm);
    setFormErrors({});
    setDisplayModal(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      account_code: record.account_code || "",
      account_name: record.account_name || "",
      account_type: record.account_type || "asset",
      parent_account_id: record.parent_account_id || "",
      account_description: record.account_description || "",
      is_active: String(record.is_active ?? "1"),
      is_cash_account: Number(record.is_cash_account) === 1,
    });
    setFormErrors({});
    setDisplayModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const typeLabel = (type) =>
    ACCOUNT_TYPES.find((t) => t.value === type)?.label || "—";

  const parentName = (parentId) => {
    if (!parentId) return "—";
    const parent = accounts.find(
      (a) => String(a.account_id) === String(parentId)
    );
    return parent ? `${parent.account_code} · ${parent.account_name}` : "—";
  };

  // Counts driving the stat cards, which double as one-click type filters.
  const countsByType = accounts.reduce((acc, a) => {
    acc[a.account_type] = (acc[a.account_type] || 0) + 1;
    return acc;
  }, {});

  const hasFilters = typeFilter !== "" || search !== "";

  const clearFilters = () => {
    setTypeFilter("");
    setSearch("");
  };

  // Order parents first with their children directly beneath, so the list
  // reads as the account hierarchy rather than raw insertion order.
  const visibleAccounts = (() => {
    const filtered = typeFilter
      ? accounts.filter((a) => a.account_type === typeFilter)
      : accounts;

    const byCode = (a, b) =>
      String(a.account_code).localeCompare(String(b.account_code), undefined, {
        numeric: true,
      });

    const visibleIds = new Set(filtered.map((a) => String(a.account_id)));
    // A child whose parent is filtered out is treated as a root, so it never
    // disappears from the list.
    const isRoot = (a) =>
      !a.parent_account_id || !visibleIds.has(String(a.parent_account_id));

    const childrenOf = (parentId) =>
      filtered
        .filter(
          (a) =>
            a.parent_account_id &&
            String(a.parent_account_id) === String(parentId)
        )
        .sort(byCode);

    const ordered = [];
    const walk = (account, depth) => {
      ordered.push({ ...account, depth });
      childrenOf(account.account_id).forEach((child) => walk(child, depth + 1));
    };
    filtered.filter(isRoot).sort(byCode).forEach((a) => walk(a, 0));
    return ordered;
  })();

  /* ---------------- UI ---------------- */
  return (
    <div className="finance-page-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <FinanceBreadcrumb
        items={
          typeFilter
            ? [{ label: "Chart of Accounts" }, { label: typeLabel(typeFilter) }]
            : [{ label: "Chart of Accounts" }]
        }
      />
      <div className="special-header panel-white-all">
        <h4 className="page-title">Chart of Accounts</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={openCreate}>
            Create Account
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* STAT CARDS — also act as one-click type filters */}
        <div className="fin-stat-row">
          <button
            type="button"
            className={`fin-stat-card fin-accent-all${
              typeFilter === "" ? " is-active" : ""
            }`}
            title="Show accounts of every type"
            onClick={() => setTypeFilter("")}
          >
            <span className="fin-stat-label">All Accounts</span>
            <span className="fin-stat-value">{accounts.length}</span>
          </button>

          {ACCOUNT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`fin-stat-card fin-accent-${t.value}${
                typeFilter === t.value ? " is-active" : ""
              }`}
              title={
                typeFilter === t.value
                  ? `Showing ${t.label} accounts — click to clear`
                  : `Show only ${t.label} accounts`
              }
              onClick={() =>
                setTypeFilter(typeFilter === t.value ? "" : t.value)
              }
            >
              <span className="fin-stat-label">{t.label}</span>
              <span className="fin-stat-value">{countsByType[t.value] || 0}</span>
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <div className="yc-list-filter">
          <Form.Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            title="Filter by category"
          >
            <option value="">All Categories</option>
            {ACCOUNT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
          <Form.Control
            placeholder="Search code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {hasFilters && (
            <Button variant="secondary" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        {/* TABLE HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-7">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Account Code</div>
            <div className="yc-list-cell">Account Name</div>
            <div className="yc-list-cell">Type</div>
            <div className="yc-list-cell">Parent Account</div>
            <div className="yc-list-cell">Status</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* TABLE BODY */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : visibleAccounts.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">
              {hasFilters ? "No matching accounts" : "No accounts yet"}
            </span>
            <span className="fin-empty-text">
              {hasFilters
                ? "Nothing matches the current filters. Try a different search or account type."
                : "The chart of accounts is the backbone of the ledger — every journal entry posts against an account here. Start by creating your bank, fee income and expense accounts."}
            </span>
            {hasFilters ? (
              <Button variant="secondary" className="mt-2" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button className="btn btn-primary mt-2" onClick={openCreate}>
                Create First Account
              </Button>
            )}
          </div>
        ) : (
          <div className="yc-list-row-list">
            {visibleAccounts.map((record, index) => (
              <div key={record.account_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-7">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell fw-semibold">
                    {record.account_code}
                  </div>
                  <div
                    className="yc-list-cell"
                    style={{ paddingLeft: record.depth * 18 }}
                  >
                    <span className="fin-account-name">
                      {record.depth > 0 && (
                        <span className="fin-child-marker">└</span>
                      )}
                      {record.account_name}
                    </span>
                  </div>
                  <div className="yc-list-cell">
                    <span className={`fin-type-pill fin-type-${record.account_type}`}>
                      {typeLabel(record.account_type)}
                    </span>
                  </div>
                  <div className="yc-list-cell">
                    {parentName(record.parent_account_id)}
                  </div>
                  <div className="yc-list-cell">
                    <span
                      className={
                        Number(record.is_active) === 1
                          ? "fin-status fin-status-active"
                          : "fin-status fin-status-inactive"
                      }
                    >
                      {Number(record.is_active) === 1 ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="View Ledger"
                      onClick={() => openLedger(record)}
                    >
                      <FaListUl size={15} />
                    </button>
                    <button
                      className="yc-action-icon"
                      title="Edit"
                      onClick={() => openEdit(record)}
                    >
                      <FaEdit size={16} />
                    </button>
                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(record.account_id);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal
        show={displayModal}
        onHide={() => !saving && setDisplayModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRecord ? "Edit Account" : "Create Account"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Account Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="account_code"
                  value={form.account_code}
                  isInvalid={!!formErrors.account_code}
                  onChange={handleFormChange}
                  placeholder="e.g. 1000"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.account_code}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="col-md-8">
              <Form.Group>
                <Form.Label>
                  Account Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="account_name"
                  value={form.account_name}
                  isInvalid={!!formErrors.account_name}
                  onChange={handleFormChange}
                  placeholder="e.g. Cash at Bank"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.account_name}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Account Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="account_type"
                  value={form.account_type}
                  onChange={handleFormChange}
                >
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-5">
              <Form.Group>
                <Form.Label>Parent Account</Form.Label>
                <Form.Select
                  name="parent_account_id"
                  value={form.parent_account_id}
                  onChange={handleFormChange}
                >
                  <option value="">None (top level)</option>
                  {accounts
                    .filter(
                      (a) =>
                        !editingRecord ||
                        a.account_id !== editingRecord.account_id
                    )
                    .map((a) => (
                      <option key={a.account_id} value={a.account_id}>
                        {a.account_code} · {a.account_name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="is_active"
                  value={form.is_active}
                  onChange={handleFormChange}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Check
                type="checkbox"
                id="coa-is-cash"
                name="is_cash_account"
                checked={form.is_cash_account}
                onChange={handleFormChange}
                label="This is a cash / bank account (included in the Cash Flow report)"
              />
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="account_description"
                  value={form.account_description}
                  onChange={handleFormChange}
                  placeholder="What this account is used for"
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDisplayModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRM */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Account"
        message="Deleting a GL account is only possible when it has no posted journal lines. Continue?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />

      {/* ACCOUNT LEDGER DRILL-IN */}
      <Modal
        show={showLedger}
        onHide={() => setShowLedger(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Account Ledger
            {ledgerAccount
              ? ` — ${ledgerAccount.account_code} · ${ledgerAccount.account_name}`
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="yc-list-filter mb-2" style={{ margin: 0 }}>
            <Form.Control
              type="date"
              value={ledgerFrom}
              onChange={(e) => setLedgerFrom(e.target.value)}
              title="From date"
            />
            <Form.Control
              type="date"
              value={ledgerTo}
              onChange={(e) => setLedgerTo(e.target.value)}
              title="To date"
            />
            <Button
              className="btn btn-primary"
              disabled={ledgerLoading || !ledgerAccount}
              onClick={() =>
                fetchLedger(ledgerAccount.account_id, ledgerFrom, ledgerTo)
              }
            >
              {ledgerLoading ? "Loading..." : "Apply"}
            </Button>
          </div>

          {ledgerData && (
            <div className="d-flex gap-4 my-3 flex-wrap">
              <div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Opening Balance
                </div>
                <div className="fw-bold">{money(ledgerData.opening_balance)}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Closing Balance
                </div>
                <div className="fw-bold">{money(ledgerData.closing_balance)}</div>
              </div>
            </div>
          )}

          {ledgerLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : !ledgerData ? (
            <div className="text-muted text-center py-3">No data.</div>
          ) : (ledgerData.lines || []).length === 0 ? (
            <div className="text-muted text-center py-3">
              No postings to this account for the selected range.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="lib-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Narration</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.lines.map((l) => (
                    <tr key={l.line_id}>
                      <td>
                        {l.journal_date
                          ? new Date(l.journal_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>{l.journal_reference || "—"}</td>
                      <td title={l.line_description}>
                        {l.line_description || l.journal_narration || "—"}
                      </td>
                      <td>{Number(l.debit) > 0 ? money(l.debit) : "—"}</td>
                      <td>{Number(l.credit) > 0 ? money(l.credit) : "—"}</td>
                      <td className="fw-semibold">{money(l.running_balance)}</td>
                      <td>{l.journal_status || "posted"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLedger(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ChartOfAccounts;
